/**
 * FundraiserApp.tsx - Main Fundraiser Display Component
 *
 * This is the primary component that renders a complete fundraiser page with all sections.
 * It handles layout zones, section visibility, donation flows, and responsive design.
 * Supports both live fundraiser display and preview mode for the builder.
 *
 * Key Features:
 * - Dynamic layout system with configurable zones (top, left, right, bottom)
 * - Section visibility management and conditional rendering
 * - Donation modal integration (popup vs embedded)
 * - Promoter context and highlighting
 * - Custom color theming and responsive design
 * - Social sharing and fundraiser status handling
 *
 * Props:
 * - isPreview?: boolean - Whether in builder preview mode
 * - promoterId?: string - Promoter ID for tracking and highlighting
 * - enableDragHandles?: boolean - Enable drag handles for builder mode
 *
 * Dependencies: MobX store, styled-components, donation components, section components
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft } from "lucide-react";
import { createPortal } from "react-dom";
import * as Icons from "lucide-react";
import Header from "./Header";
import { ProgressSection } from "./ProgressSection";
import { ImpactStats } from "./ImpactStats";
import { MissionSection } from "./MissionSection";
import { ImpactSection } from "./ImpactSection";
import { MediaSection } from "./MediaSection";
import { PromoterLeaderboard } from "./PromoterLeaderboard";
import { RecentDonations } from "./RecentDonations";
import { CallToAction } from "./CallToAction";
import { AdvancedDonationModal } from "./AdvancedDonationModal";
import { DonationCapture } from "./donation-capture/DonationCapture";
import { fundraiserStore } from "../stores/FundraiserStore";
import FundraiserDisabled from "./FundraiserDisabled";
import { SocialShareModal } from "./SocialShareModal";
import { lightenColor } from "../utils/colorUtils";
import {
  DonationFormType,
  DonationPopupModalType,
  FundRaiserStatus,
  FundRaiserTemplateLayoutType,
  IconType,
} from "../enums";
import {
  ColorModel,
  CreateFundraiserModel,
  FundraiserCustomColorConfig,
} from "../models";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import {
  AuthService,
  Loader,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { Fundraiser } from "../models/FundraisersGridModels";
import { NavigateFunction, Params } from "react-router-dom";
import ToastService from "./ToastService";
import { colorTemplates } from "./fundraiser-builder";

const AppContainer = styled.div<{ backgroundColor?: string }>`
  min-height: 100vh;
  background: ${(props) => props.backgroundColor || "#f8fafc"};
  position: relative;
  /* The sticky header changes its own height on scroll. Scroll anchoring
     would compensate by moving scrollTop, which feeds that height change
     straight back into the header's scroll handler. Opt this subtree out. */
  overflow-anchor: none;
`;

const BackToDashboard = styled.button`
  position: fixed;
  top: 24px;
  left: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #e2e8f0;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 100px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0 20px 80px;
    max-width: 100%;
  }
`;

// Layout zone containers
const TopZone = styled.div<{ zoneWidth: number }>`
  width: 100%;
  display: ${(props) => (props.zoneWidth === 0 ? "none" : "flex")};
  flex-direction: column;
  gap: 32px;
  margin: 32px 0;

  @media (max-width: 768px) {
    gap: 24px;
    margin: 24px 0;
  }
`;

const MiddleZoneContainer = styled.div<{
  leftWidth: number;
  rightWidth: number;
}>`
  display: ${(props) =>
    props.leftWidth === 0 && props.rightWidth === 0 ? "none" : "grid"};
  grid-template-columns: ${(props) => {
    if (props.leftWidth === 0) return "1fr";
    if (props.rightWidth === 0) return "1fr";
    return `${props.leftWidth}fr ${props.rightWidth}fr`;
  }};
  gap: 32px;
  margin: 32px 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: 768px) {
    gap: 20px;
    margin: 24px 0;
  }
`;

const LeftZone = styled.div<{ zoneWidth: number }>`
  display: ${(props) => (props.zoneWidth === 0 ? "none" : "flex")};
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const RightZone = styled.div<{ zoneWidth: number }>`
  display: ${(props) => (props.zoneWidth === 0 ? "none" : "flex")};
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const BottomZone = styled.div<{ zoneWidth: number }>`
  width: 100%;
  display: ${(props) => (props.zoneWidth === 0 ? "none" : "flex")};
  flex-direction: column;
  gap: 16px;
  margin: 16px 0 0 0;
  padding-bottom: 20px;

  @media (max-width: 768px) {
    gap: 20px;
    margin: 20px 0 0 0;
  }
`;

const ProgressStatsWrapper = styled.div<{ customColors?: any }>`
  background: ${(props) => props.customColors?.accent || "#ecfdf5"};
  border-radius: 24px;
  padding: 32px;
  margin: 32px -8px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    pointer-events: none;
  }
`;

const StableMediaWrapper = styled.div`
  /* Total reserved height = image + indicators + title + description */
  display: flex;
  flex-direction: column;
  contain: layout paint;
  max-height: 820px;
  @media (max-width: 768px) {
    .fundraiser-media-wrapper p {
      min-height: calc(1.6em * 2);
      max-height: calc(1.6em * 2);
      -webkit-line-clamp: 2;
    }
  }
`;

const MediaTextStabilizer = styled.div`
  .fundraiser-media-wrapper h3 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 32px;
  }

  .fundraiser-media-wrapper p {
    min-height: 72px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: calc(1.6em * 3);
  }
`;
const MediaOverride = styled.div`
  .fundraiser-media-wrapper img {
    object-fit: cover;
  }
`;

interface FundraiserAppProps {
  isPreview?: boolean;
  promoterId?: string;
  enableDragHandles?: boolean;
  fundraiserId?: string;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
}

const FundraiserApp = observer(
  ({
    isPreview = false,
    promoterId,
    enableDragHandles = false,
    fundraiserId,
    navigate,
    params,
  }: FundraiserAppProps) => {
    const [showLoader, setShowLoader] = useState(true);

    const shortUniqueId = params["shortUniqueId"] || "";
    const nameParam = params["fundraiserID"];
    let name = "";
    if (UtilityMethods.isValidObjectId(nameParam || "")) {
      name = "";
    } else {
      name = nameParam || "";
    }

    useEffect(() => {
      document.body.style.overflow = "unset";
    }, []);

    React.useEffect(() => {
      if (fundraiserId) {
        fetchFundraiserData();
      } else {
        if (name) {
          fetchPublicURLFundraiserData(name);
        } else {
          fetchFundraiserData();
        }
      }
    }, [fundraiserStore.selectedFundraiserId, isPreview, name]);

    const [showDonationModal, setShowDonationModal] = useState(false);
    const [showSocialShareModal, setShowSocialShareModal] = useState(false);

    const fetchPublicURLFundraiserData = async (fundraiserName: string) => {
      try {
        setShowLoader(true);
        const response = await firstValueFrom(
          FundraiserService.getFundraiserByUrl(
            shortUniqueId || "",
            fundraiserName,
            promoterId || "",
          ),
        );
        if (response.result) {
          const data = CreateFundraiserModel.deserializeAPIModel(
            response.result,
            !isPreview,
          );
          fundraiserStore.setFundraiserDetails(data);
          fundraiserStore.setBuilderData(data);
          fundraiserStore.initializeBuilder(data);
          setShowLoader(false);
        } else {
          navigate("/page-not-found");
        }
      } catch (error) {
        console.error("Failed to load public URL fundraiser data", error);
      }
    };

    const fetchFundraiserData = async () => {
      try {
        setShowLoader(true);
        const response = await firstValueFrom(
          FundraiserService.getFundraiserData(
            fundraiserStore.selectedFundraiserId,
            promoterId || "",
          ),
        );
        if (response.result) {
          const data = CreateFundraiserModel.deserializeAPIModel(
            response.result,
            !isPreview,
          );
          fundraiserStore.setFundraiserDetails(data);
          fundraiserStore.setBuilderData(data);
          fundraiserStore.initializeBuilder(data);
          setShowLoader(false);
        } else {
          navigate("/page-not-found");
        }
      } catch (error) {
        console.error("Failed to load fundraisers", error);
      }
    };

    // Get the donation form type from builder data or default to embedded
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const donationFormType =
      selectedFundraiser?.donationFormType || DonationFormType.Embedded;
    const popupModalType =
      selectedFundraiser?.popupModalType || DonationPopupModalType.Simple;
    const selectedLayout =
      selectedFundraiser?.selectedLayout ||
      FundRaiserTemplateLayoutType.Layout1;

    const sectionVisibility = selectedFundraiser?.sectionVisibility || {
      header: true,
      fundraiserProgress: true,
      promoterProgress: true,
      stats: true,
      mission: true,
      impact: true,
      media: true,
      donation: true,
      promoters: true,
      recent: true,
      cta: true,
      description: false,
    };

    // Check fundraiser status and render appropriate component (skip for preview mode)
    if (
      !isPreview &&
      selectedFundraiser &&
      (selectedFundraiser.status === FundRaiserStatus.Inactive ||
        selectedFundraiser.status === FundRaiserStatus.Completed)
    ) {
      return (
        <FundraiserDisabled isPreview={isPreview} promoterId={promoterId} />
      );
    }

    const isDraft =
      !isPreview && selectedFundraiser?.status === FundRaiserStatus.Draft;

    // Get zone widths from current layout
    const getZoneWidths = () => {
      const layoutTemplates = {
        Layout1: { topZone: 100, leftZone: 67, rightZone: 33, bottomZone: 100 },
        Layout2: { topZone: 100, leftZone: 33, rightZone: 67, bottomZone: 100 },
        Layout3: { topZone: 100, leftZone: 50, rightZone: 50, bottomZone: 100 },
        Layout4: { topZone: 0, leftZone: 67, rightZone: 33, bottomZone: 100 },
        Layout5: { topZone: 100, leftZone: 0, rightZone: 0, bottomZone: 0 },
      };

      return (
        layoutTemplates[selectedLayout] || {
          topZone: 100,
          leftZone: 67,
          rightZone: 33,
          bottomZone: 100,
        }
      );
    };

    const zoneWidths = getZoneWidths();

    // Get sections assigned to each zone, filtered by visibility
    const getZoneSections = (
      zone: keyof typeof fundraiserStore.layoutZones,
    ) => {
      const zoneSections = fundraiserStore.layoutZones[zone] || [];

      // Handle sponsor section visibility based on banner image
      let filteredSections = zoneSections.filter((sectionId) => {
        // Check section visibility
        if (sectionVisibility[sectionId] === false) return false;

        // Special handling for sponsor section
        if (sectionId === "sponsor") {
          return !!fundraiserStore.builderData?.bannerImage;
        }

        return true;
      });

      return filteredSections;
    };

    // Function to render individual section components
    const renderSectionComponent = (sectionId: string, zoneType: string) => {
      const isInRightZone = zoneType === "right-column";
      const isFullWidth = zoneType === "full-width";

      switch (sectionId) {
        case "fundraiserProgress":
        case "promoterProgress":
          // Smart rendering to prevent duplicates when both are visible
          if (
            sectionVisibility.fundraiserProgress &&
            sectionVisibility.promoterProgress
          ) {
            // Only render from fundraiserProgress case, promoterProgress returns null
            return sectionId === "fundraiserProgress" ? (
              <ProgressSection
                fundraiser={selectedFundraiser}
                promoterProgress={selectedFundraiser.promoterProgress}
                showFundraiser={true}
                showPromoter={true}
                promoterId={promoterId}
                forceStacked={isInRightZone}
              />
            ) : null;
          } else if (
            sectionVisibility.fundraiserProgress &&
            sectionId === "fundraiserProgress"
          ) {
            return (
              <ProgressSection
                fundraiser={selectedFundraiser}
                showFundraiser={true}
                showPromoter={false}
                forceStacked={isInRightZone}
              />
            );
          } else if (
            sectionVisibility.promoterProgress &&
            sectionId === "promoterProgress" &&
            selectedFundraiser.promoterProgress?.id
          ) {
            return (
              <ProgressSection
                fundraiser={selectedFundraiser}
                showFundraiser={false}
                showPromoter={true}
                promoterId={promoterId}
                forceStacked={isInRightZone}
              />
            );
          }
          return null;

        case "stats":
          return <ImpactStats forceStacked={isInRightZone} />;

        case "mission":
          return (
            <MissionSection
              customColors={selectedFundraiser.getCustomColors()}
            />
          );

        case "impact":
          return (
            <ImpactSection
              isPreviewMode={false}
              impacts={selectedFundraiser.impact || []}
              forceStacked={isInRightZone}
              zoneType={
                zoneType as "full-width" | "left-column" | "right-column"
              }
            />
          );

        case "media":
          return selectedFundraiser.mediaItems.length > 0 ? (
            <StableMediaWrapper className="fundraiser-media-wrapper">
              <MediaSection
                fundraiser={selectedFundraiser}
                mediaItems={selectedFundraiser.mediaItems}
                isPreviewMode={false}
              />
            </StableMediaWrapper>
          ) : null;

        case "description":
          const descriptionText = selectedFundraiser?.description;
          if (!descriptionText) return null;
          return (
            <div
              style={{
                background: "#ffffff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #f1f5f9",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  color: selectedFundraiser.getCustomColors().text
                    ? lightenColor(
                        selectedFundraiser.getCustomColors().text.hex,
                        25,
                      )
                    : "#64748b",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                {/* {builderData?.description || selectedFundraiser?.description || 'Building wells and water systems to provide clean, safe drinking water to communities in need around the world.'} */}
                {selectedFundraiser?.description}
              </p>
            </div>
          );

        case "promoters":
          return (
            <PromoterLeaderboard
              highlightPromoterId={promoterId}
              leaderboard={selectedFundraiser?.leaderboard || []}
            />
          );

        case "recent":
          return (
            <RecentDonations
              donations={selectedFundraiser?.donationsBoard || []}
            />
          );

        case "cta":
          // Determine if CTA is in a full-width zone
          const ctaZone = Object.entries(fundraiserStore.layoutZones).find(
            ([zoneKey, sections]) => sections.includes("cta"),
          );
          const isCtaInFullWidth =
            ctaZone &&
            (ctaZone[0] === "topZone" || ctaZone[0] === "bottomZone");

          return (
            <CallToAction
              isDisabled={
                !fundraiserStore.isDonationAllowed(
                  false,
                  selectedFundraiser.status,
                )
              }
              onDonateClick={handleDonateClick}
              isPreview={isPreview}
              isBanner={
                zoneType === "full-width" &&
                !(
                  selectedFundraiser?.ctaIconImageChoice === IconType.Image &&
                  selectedFundraiser?.ctaImage
                )
              }
              customColors={selectedFundraiser.getCustomColors()}
              buttonText={selectedFundraiser?.ctaButtonText || "Donate"}
              showIcon={
                selectedFundraiser?.ctaIconImageChoice !== IconType.None
              }
              showButtonOnImage={selectedFundraiser?.showCtaButton !== false}
              isFullWidth={isCtaInFullWidth}
            />
          );

        case "donation":
          if (donationFormType === DonationFormType.Embedded) {
            return (
              <div id="donation-capture">
                <DonationCapture
                  promoterId={promoterId}
                  isVisible={true}
                  onClose={() => {}}
                  isEmbedded={true}
                  shortUniqueId={shortUniqueId}
                />
              </div>
            );
          }
          return null;

        case "sponsor":
          if (selectedFundraiser?.bannerImage?.fullURL) {
            return (
              <div
                style={{
                  width: "100%",
                  padding: "0",
                  margin: "16px 0",
                }}
              >
                <img
                  src={selectedFundraiser.bannerImage.fullURL}
                  alt="Sponsor Banner"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e2e8f0",
                  }}
                />
              </div>
            );
          }
          return null;

        default:
          return null;
      }
    };

    // Function to render a zone with its sections
    const renderZone = (zoneId: keyof typeof fundraiserStore.layoutZones) => {
      const zoneSections = getZoneSections(zoneId);
      const zoneType =
        zoneId.includes("top") || zoneId.includes("bottom")
          ? "full-width"
          : zoneId.includes("left")
            ? "left-column"
            : "right-column";

      // Only render sections that exist and are visible
      return zoneSections
        .map((sectionId) => renderSectionComponent(sectionId, zoneType))
        .filter(Boolean); // Remove null components
    };

    const handleDonateClick = () => {
      // Store the current promoter context for leaderboard highlighting
      if (promoterId) {
        sessionStorage.setItem("highlightPromoterId", promoterId);
      } else {
        sessionStorage.removeItem("highlightPromoterId");
      }

      if (isDraft) {
        ToastService.showSuccess(
          "Fundraiser is not active and cannot process a donation",
        );
        return;
      }

      if (donationFormType === DonationFormType.Popup) {
        if (popupModalType === DonationPopupModalType.DonationItem) {
          // Use AdvancedDonationModal for donation item
          setShowDonationModal(true);
        } else {
          // Use DonationCapture for simple form
          setShowDonationModal(true);
        }
      } else {
        // Scroll to donation capture form for embedded type
        const donationCapture = document.getElementById(
          "donation-capture-header",
        );
        if (donationCapture) {
          const targetPosition =
            donationCapture.getBoundingClientRect().top + window.scrollY - 125;
          window.scrollTo({ top: targetPosition, behavior: "smooth" });
        }
      }
    };

    const handleShareClick = () => {
      setShowSocialShareModal(true);
    };

    if (showLoader) {
      return <>{new Loader(true).loaderComponent}</>;
    }

    return (
      <AppContainer
        backgroundColor={
          selectedFundraiser.getCustomColors().background.rgba ||
          selectedFundraiser.getCustomColors().background.hex
        }
      >
        {isPreview && (
          <BackToDashboard
            onClick={() => navigate("/fundraiser/fundraiser-details")}
          >
            <ArrowLeft size={20} />
          </BackToDashboard>
        )}

        {/* Header - always visible and fixed */}
        {sectionVisibility.header && (
          <Header
            onDonateClick={handleDonateClick}
            onShareClick={handleShareClick}
            customColors={selectedFundraiser.getCustomColors()}
            isPreview={isPreview}
          />
        )}
        <MediaTextStabilizer>
          <MediaOverride>
            <ContentWrapper>
              {/* Top Zone - Full Width */}
              <TopZone zoneWidth={zoneWidths.topZone}>
                {renderZone("topZone")}
              </TopZone>

              {/* Middle Zone - Two Columns */}
              <MiddleZoneContainer
                leftWidth={zoneWidths.leftZone}
                rightWidth={zoneWidths.rightZone}
              >
                <LeftZone zoneWidth={zoneWidths.leftZone}>
                  {renderZone("leftZone")}
                </LeftZone>

                <RightZone zoneWidth={zoneWidths.rightZone}>
                  {renderZone("rightZone")}
                </RightZone>
              </MiddleZoneContainer>

              {/* Bottom Zone - Full Width */}
              <BottomZone zoneWidth={zoneWidths.bottomZone}>
                {renderZone("bottomZone")}
              </BottomZone>
            </ContentWrapper>
          </MediaOverride>
        </MediaTextStabilizer>

        {showDonationModal &&
          createPortal(
            // popupModalType === DonationPopupModalType.DonationItem ? (
            //   <AdvancedDonationModal
            //     isVisible={true}
            //     onClose={() => setShowDonationModal(false)}
            //     isPreview={isPreview}
            //   />
            // ) : (
            //   <DonationCapture
            //     promoterId={promoterId}
            //     isVisible={true}
            //     onClose={() => setShowDonationModal(false)}
            //     isEmbedded={false}
            //   />
            // )
            <DonationCapture
              promoterId={promoterId}
              isVisible={true}
              onClose={() => setShowDonationModal(false)}
              isEmbedded={donationFormType === DonationFormType.Embedded}
              shortUniqueId={shortUniqueId || ""}
            />,
            document.body,
          )}

        {showSocialShareModal &&
          createPortal(
            <SocialShareModal
              gallerySettings={fundraiserStore.gallerySettings}
              isVisible={true}
              onClose={() => setShowSocialShareModal(false)}
              shortUniqueId={shortUniqueId || ""}
            />,
            document.body,
          )}
      </AppContainer>
    );
  },
);

export default withRouter(FundraiserApp);
